import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from '@/services/api';
import { getAuthedBaseQuery } from '@/services/rtkAuthedBaseQuery';

const getApiBaseUrl = (): string => {
  // Priority 1: explicit env var (production builds, Cloud Run)
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL;
  }
  // Priority 2: relative URL — routes through Vite dev proxy (/api → http://backend:3000)
  // The Vite proxy in vite.config.ts already handles /api → http://backend:3000.
  // This makes the request same-origin (no CORS) regardless of frontend hostname
  // (localhost, Tailscale, LAN — all work without backend CORS config changes).
  return '/api/v1';
};

export interface DescriptorCatalogEntry {
  // Legacy shape fields (kept for backward compatibility)
  descriptorKey: string;
  label?: string;
  dataSource?: string;
  metricType?: string;
  metric?: string;
  widgetType?: string;
  // New backend shape fields (optional — gracefully absent on older responses)
  displayName?: string;
  category?: string;
  supportsPin?: boolean;
  compatibleVisualTypes?: string[];
  minCellSize?: { w: number; h: number };
  availableMetrics?: string[];
  description?: string;
  resolverName?: string;
  defaultConfig?: Record<string, unknown>;
  showInPalette?: boolean;
  hidden?: boolean;
  isDeferred?: boolean;
}

export const dashboardsApi = createApi({
  reducerPath: 'dashboardsApi',
  baseQuery: getAuthedBaseQuery(getApiBaseUrl()),
  endpoints: (builder) => ({
    getDescriptorCatalog: builder.query<DescriptorCatalogEntry[], void>({
      query: () => endpoints.dashboards.descriptors,
      transformResponse: (response: { data?: DescriptorCatalogEntry[] } | DescriptorCatalogEntry[]) => {
        if (Array.isArray(response)) return response;
        return response.data ?? [];
      },
    }),
  }),
});

export const { useGetDescriptorCatalogQuery } = dashboardsApi;